import { useState } from "react";
import { faAngleRight, faArrowsRepeat } from "@fortawesome/pro-regular-svg-icons";

import { parseNumber } from "common/utils/numbers";
import { pluralized, titleCased } from "common/utils/strings";
import Icon from "common/components/Icon";

import Box from "client/common/components/layouts/Box";
import Switch from "client/common/components/inputs/Switch";
import PaymentMethod from "client/common/components/content/PaymentMethod";
import TextChip from "client/common/components/content/TextChip";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useRemovePaymentDrawer } from "client/accounts/stackables";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { useClientQuery, useClientUserQuery, useUpdateClientMutation } from "../../hooks";
import Tooltip from "../../../common/components/content/Tooltip";

import styles from "../../styles/UpdatePaymentForm.module.less";

export type AccountUpdatePaymentDrawerProps = {
  id: number;
};

const AccountUpdatePaymentDrawer = ({ id }: AccountUpdatePaymentDrawerProps) => {
  const { close } = useCurrentDrawer();
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();
  const {
    data: clientUser,
    isLoading: isClientUserLoading,
    error: clientUserError,
  } = useClientUserQuery();
  const {
    mutate: updateClient,
    isLoading: isUpdating,
    error: updateClientError,
  } = useUpdateClientMutation({ onSuccess: close });
  const { open: openRemovePaymentDrawer } = useRemovePaymentDrawer();

  const payment = client?.payment_methods.find((method) => method.id === id);
  const isCurrentlyPrimary = client?.primary_payment_method?.id === id;

  const [isPrimary, setIsPrimary] = useState(false);

  const toggleIsPrimary = () => {
    setIsPrimary((p) => !p);
  };

  if (isClientLoading || isClientUserLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!payment || !client || !clientUser || clientError || clientUserError) {
    return (
      <DrawerLayout.FetchError entityName="payment method" error={clientError || clientUserError} />
    );
  }

  const isPrimaryClientUser = client?.primary_client_user.id === clientUser?.id;

  const paymentGroup = (
    <DrawerLayout.Form.Group>
      <Box className={styles.paymentBox}>
        <div className={styles.payment}>
          <PaymentMethod
            method={payment}
            styles={{
              label: styles.paymentLabel,
              description: styles.description,
            }}
          />
          {isCurrentlyPrimary && <TextChip>Primary</TextChip>}
        </div>
      </Box>
      {!!payment.relevant_subscription_count && (
        <TextChip>
          <Icon icon={faArrowsRepeat} className={styles.autoshipIcon} />
          <span>
            {payment.relevant_subscription_count}{" "}
            {pluralized(payment.relevant_subscription_count, "Autoship")} use this method
          </span>
        </TextChip>
      )}
    </DrawerLayout.Form.Group>
  );

  const handleRemovePayment = () => {
    openRemovePaymentDrawer({ id, paymentGroup, isCurrentlyPrimary, onSuccess: close });
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update payment method" onClose={close} />
      <DrawerLayout.Form
        submitText="Update Method"
        onSubmit={() => updateClient({ primary_payment_method: parseNumber(id) })}
        canSubmit={!isUpdating && isPrimary}
        isLoading={isUpdating}
        error={updateClientError}
        fieldErrors={[updateClientError?.errors.primary_payment_method]}
        // update doesn't do anything for the primary payment method
        hideSubmit={isCurrentlyPrimary}
      >
        {paymentGroup}
        {!isCurrentlyPrimary && (
          <DrawerLayout.Form.Group>
            <Tooltip
              isEnabled={!isPrimaryClientUser}
              content="Only primary account owners can set a primary payment method."
              position="below"
            >
              <Box className={styles.option}>
                <button
                  type="button"
                  onClick={toggleIsPrimary}
                  className={styles.button}
                  disabled={!isPrimaryClientUser}
                >
                  <p className={styles.label}>Set primary payment method</p>
                </button>
                <div className={styles.switch}>
                  <Switch
                    value={isPrimary}
                    onChange={toggleIsPrimary}
                    disabled={!isPrimaryClientUser}
                  />
                </div>
              </Box>
            </Tooltip>
          </DrawerLayout.Form.Group>
        )}
        <DrawerLayout.Form.Group>
          <Box className={styles.option}>
            <button type="button" onClick={handleRemovePayment} className={styles.button}>
              <p className={styles.label}>Remove payment method</p>
              <p className={styles.icon}>
                <Icon icon={faAngleRight} />
              </p>
            </button>
          </Box>
        </DrawerLayout.Form.Group>
        {isPrimary && (
          <DrawerLayout.Form.Group>
            <p className={styles.label}>
              {client.primary_payment_method
                ? `This will replace ${titleCased(client.primary_payment_method.brand)} ****${
                    client.primary_payment_method?.last_four
                  }`
                : "This will be set"}{" "}
              as your primary payment method. This will be the default for future orders.
            </p>
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default AccountUpdatePaymentDrawer;
